import { transport, BASE_PATH } from "@/src/config/apiConfig.js";

export default {
  getBasePath() {
    return BASE_PATH;
  },

  isDefined(val) {
    return typeof val !== "undefined" && val !== null;
  },

  async getVoices() {
    try {
      let res = await transport.get(`/api/voice_agents/voices`);
      return res.data;
    } catch (error) {
      console.error("Error fetching voices:", error);
      throw error;
    }
  },

  async createAgentBehaviour(payload) {
    try {
      if (!payload) {
        throw new Error("Payload is required");
      }
      let res = await transport.post(
        `/api/voice_agents/create_agent_behaviour`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error("Error creating agent behaviour:", error);
      throw error;
    }
  },

  async updateAgentBehaviour(agentId, payload) {
    try {
      if (!agentId) {
        throw new Error("Agent ID is required");
      }
      if (!payload) {
        throw new Error("Payload is required");
      }
      let res = await transport.patch(
        `/api/voice_agents/update_agent_behaviour/agent/${agentId}`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error("Error updating agent behaviour:", error);
      throw error;
    }
  },

  async createKnowledgeBase(payload) {
    try {
      if (!payload) {
        throw new Error("Payload is required");
      }
      let res = await transport.post(
        `/api/voice_agents/knowledge_bases`,
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return res.data;
    } catch (error) {
      console.error("Error creating knowledge base:", error);
      throw error;
    }
  },

  async getKnowledgeBases(agentId) {
    try {
      if (!agentId) {
        throw new Error("Agent ID is required");
      }
      let res = await transport.get(
        `/api/voice_agents/knowledge_bases/agent/${agentId}`,
      );
      return res.data;
    } catch (error) {
      console.error("Error fetching knowledge bases:", error);
      throw error;
    }
  },

  async updateKnowledgeBase(knowledgeBaseId, payload) {
    try {
      if (!knowledgeBaseId) {
        throw new Error("Knowledge Base ID is required");
      }
      if (!payload) {
        throw new Error("Payload is required");
      }
      let res = await transport.put(
        `/api/voice_agents/knowledge_bases/${knowledgeBaseId}`,
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return res.data;
    } catch (error) {
      console.error("Error updating knowledge base:", error);
      throw error;
    }
  },

  async deleteKnowledgeBase(knowledgeBaseId) {
    try {
      if (!knowledgeBaseId) {
        throw new Error("Knowledge Base ID is required");
      }
      let res = await transport.delete(
        `/api/voice_agents/knowledge_bases/${knowledgeBaseId}`,
      );
      return res.data;
    } catch (error) {
      console.error("Error deleting knowledge base:", error);
      throw error;
    }
  },

  async getKnowledgeBasesByAgent(agentId) {
    try {
      if (!agentId) {
        throw new Error("Agent ID is required");
      }
      let res = await transport.get(
        `/api/voice_agents/knowledge_bases/agent/${agentId}`,
      );
      return res.data;
    } catch (error) {
      console.error("Error fetching knowledge bases:", error);
      throw error;
    }
  },

  async createAdvancedSettings(agentId, payload) {
    try {
      if (!agentId) {
        throw new Error("Agent ID is required");
      }
      if (!payload) {
        throw new Error("Payload is required");
      }
      let res = await transport.post(
        `/api/voice_agents/agents_advance_setting/agent/${agentId}`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error("Error creating advanced settings:", error);
      throw error;
    }
  },

  async getVoiceAgentById(agentId) {
    try {
      if (!agentId) {
        throw new Error("Agent ID is required");
      }
      let res = await transport.get(`/api/voice_agents/agents/${agentId}`);
      return res.data;
    } catch (error) {
      console.error("Error fetching voice agent:", error);
      throw error;
    }
  },

  async createDataExtraction(payload) {
    try {
      if (!payload) {
        throw new Error("Payload is required");
      }
      let res = await transport.post(
        `/api/voice_agents/data_extraction`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error("Error creating data extraction:", error);
      throw error;
    }
  },

  async getDataExtractions(agentId) {
    try {
      if (!agentId) {
        throw new Error("Agent ID is required");
      }
      let res = await transport.get(
        `/api/voice_agents/data_extraction/agent/${agentId}`,
      );
      return res.data;
    } catch (error) {
      console.error("Error fetching data extractions:", error);
      throw error;
    }
  },

  async updateDataExtraction(dataExtractionId, payload) {
    try {
      if (!dataExtractionId) {
        throw new Error("Data extraction ID is required");
      }
      if (!payload) {
        throw new Error("Payload is required");
      }
      let res = await transport.patch(
        `/api/voice_agents/data_extraction/${dataExtractionId}`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error("Error updating data extraction:", error);
      throw error;
    }
  },

  async deleteDataExtraction(dataExtractionId) {
    try {
      if (!dataExtractionId) {
        throw new Error("Data extraction ID is required");
      }
      let res = await transport.delete(
        `/api/voice_agents/data_extraction/${dataExtractionId}`,
      );
      return res.data;
    } catch (error) {
      console.error("Error deleting data extraction:", error);
      throw error;
    }
  },

  async fetchVoiceAgents(params) {
    try {
      const { page, pageSize, searchQuery, filters } = params || {};

      const queryParams = new URLSearchParams();
      if (page) queryParams.append("page", page);
      if (pageSize) queryParams.append("page_size", pageSize);
      if (searchQuery) queryParams.append("search_query", searchQuery);
      if (filters && filters.length > 0) {
        queryParams.append("filters", JSON.stringify(filters));
      }

      const url = `/api/voice_agents?${queryParams.toString()}`;
      let res = await transport.get(url);
      return res.data;
    } catch (error) {
      console.error("Error fetching voice agents:", error);
      throw error;
    }
  },

  async updatePauseStatus(agentId, isPaused) {
    try {
      if (!agentId) {
        throw new Error("Agent ID is required");
      }
      if (isPaused === undefined) {
        throw new Error("Is paused is required");
      }
      let res = await transport.patch(
        `/api/voice_agents/pause_status/agent/${agentId}`,
        { is_paused: isPaused },
      );
      return res.data;
    } catch (error) {
      console.error("Error updating pause status:", error);
      throw error;
    }
  },

  async createVoiceAction(payload) {
    try {
      if (!payload) {
        throw new Error("Payload is required");
      }
      let res = await transport.post("/api/voice_agents/voice_action", payload);
      return res.data;
    } catch (error) {
      console.error("Error creating voice action:", error);
      throw error;
    }
  },

  async getVoiceActions(agentId) {
    try {
      if (!agentId) {
        throw new Error("Agent ID is required");
      }
      let res = await transport.get(
        `/api/voice_agents/voice_action/agent/${agentId}`,
      );
      return res.data;
    } catch (error) {
      console.error("Error fetching voice actions:", error);
      throw error;
    }
  },

  async updateVoiceAction(actionId, payload) {
    try {
      if (!actionId) {
        throw new Error("Action ID is required");
      }
      if (!payload) {
        throw new Error("Payload is required");
      }
      let res = await transport.patch(
        `/api/voice_agents/voice_action/${actionId}`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error("Error updating voice action:", error);
      throw error;
    }
  },

  async deleteVoiceAction(actionId) {
    try {
      if (!actionId) {
        throw new Error("Action ID is required");
      }
      let res = await transport.delete(
        `/api/voice_agents/voice_action/${actionId}`,
      );
      return res.data;
    } catch (error) {
      console.error("Error deleting voice action:", error);
      throw error;
    }
  },

  async deleteVoiceAgent(agentId) {
    try {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({
            success: true,
            message: "Voice agent deleted successfully",
          });
        }, 300);
      });
    } catch (error) {
      console.error("Error deleting voice agent:", error);
      throw error;
    }
  },

  async cloneVoiceAgent(agentId) {
    try {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({
            success: true,
            message: "Voice agent cloned successfully",
            agent: this.generateDummyAgent(),
          });
        }, 300);
      });
    } catch (error) {
      console.error("Error cloning voice agent:", error);
      throw error;
    }
  },

  async cloneVoiceAgentCampaign(campaignId) {
    try {
      await new Promise((resolve) => setTimeout(resolve, 300));
      return {
        success: true,
        message: "Campaign cloned successfully",
      };
    } catch (error) {
      console.error("Error cloning voice agent campaign:", error);
      throw error;
    }
  },

  async deleteVoiceAgentCampaign(campaignId) {
    try {
      await new Promise((resolve) => setTimeout(resolve, 300));
      return {
        success: true,
        message: "Campaign deleted successfully",
      };
    } catch (error) {
      console.error("Error deleting voice agent campaign:", error);
      throw error;
    }
  },

  async createWebCall(voiceAgentId) {
    try {
      if (!voiceAgentId) {
        throw new Error("voice_agent_id is required");
      }
      let res = await transport.post(`/api/voice_agents/call/create_web_call`, {
        voice_agent_id: voiceAgentId,
      });
      return res.data;
    } catch (error) {
      console.error("Error creating web call:", error);
      throw error;
    }
  },

  async getCallLogs({ page = 1, page_size = 10, filters = null } = {}) {
    try {
      const params = {
        page,
        page_size,
      };

      if (filters && filters.length > 0) {
        params.filters = JSON.stringify(filters);
      }

      let res = await transport.get(`/api/voice_agents/call_logs`, { params });
      return res.data;
    } catch (error) {
      console.error("Error fetching call logs:", error);
      throw error;
    }
  },

  async exportVoiceAgentCallLogs(params) {
    try {
      let res = await transport.post(`/api/voice_agents/call_logs/export`, params);
      return res.data;
    } catch (error) {
      console.error("Error initiating voice agent call log export:", error);
      throw error;
    }
  },

  async getCallAnalysis(callId) {
    try {
      let res = await transport.get(
        `/api/voice_agents/call_analysis/${callId}`,
      );
      return res.data;
    } catch (error) {
      console.error("Error fetching call analysis:", error);
      throw error;
    }
  },

  async fetchVoiceAgentViews() {
    try {
      let res = await transport.get(`${BASE_PATH}/api/voice_agent/views`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchVoiceAgentViews: ",
        error,
      );
      throw error;
    }
  },

  async fetchVoiceAgentCampaignViews() {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/voice_agent_campaign/views`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchVoiceAgentCampaignViews: ",
        error,
      );
      throw error;
    }
  },

  async fetchVoiceAgentCallLogViews() {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/voice_agent_call_log/views`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchVoiceAgentCallLogViews: ",
        error,
      );
      throw error;
    }
  },

  async fetchBusinessReceptionistViews() {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/business_receptionist/views`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing fetchBusinessReceptionistViews: ",
        error,
      );
      throw error;
    }
  },

  async getDynamicVariables(
    agentId,
    searchQuery = "",
    page = 1,
    pageSize = 20,
  ) {
    try {
      const params = {
        search_query: searchQuery,
        page: page,
        page_size: pageSize,
      };
      let res = await transport.get(
        `/api/voice_agents/${agentId}/dynamic_variables`,
        { params },
      );

      return {
        success: res.data?.success !== false,
        variables: res.data?.data || [],
        total: res.data?.count || 0,
      };
    } catch (error) {
      console.error("Error fetching dynamic variables:", error);
      throw error;
    }
  },

  async createDynamicVariable(payload) {
    try {
      const response = await transport.post(
        `/api/voice_agents/${payload.voice_agent_id}/dynamic_variables`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error("Error creating dynamic variable:", error);
      throw error;
    }
  },

  async updateDynamicVariable(variableId, payload) {
    try {
      const response = await transport.put(
        `/api/voice_agents/dynamic_variables/${variableId}`,
        {
          variable_name: payload.variable_name,
          variable_fallback_value: payload.variable_fallback_value,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error updating dynamic variable:", error);
      throw error;
    }
  },

  async deleteDynamicVariable(variableId) {
    try {
      const response = await transport.delete(
        `/api/voice_agents/dynamic_variables/${variableId}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting dynamic variable:", error);
      throw error;
    }
  },

  async createCampaignMapping(payload) {
    try {
      const response = await transport.post(
        `/api/voice_agents/campaign_mapping`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error("Error creating campaign mapping:", error);
      throw error;
    }
  },

  async createCall(payload) {
    try {
      if (!payload) {
        throw new Error("Payload is required");
      }
      if (!payload.voice_agent_id) {
        throw new Error("Voice Agent ID is required");
      }
      if (!payload.provider) {
        throw new Error("Provider is required");
      }
      if (!payload.from_number) {
        throw new Error("From number is required");
      }
      if (!payload.to_number) {
        throw new Error("To number is required");
      }
      const response = await transport.post(
        `/api/voice_agents/create_call`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error("Error creating call:", error);
      throw error;
    }
  },

  async getVoiceModel(agentId) {
    try {
      if (!agentId) {
        throw new Error("Agent ID is required");
      }
      let res = await transport.get(`/api/voice_agents/voice_model/${agentId}`);
      return res.data;
    } catch (error) {
      console.error("Error getting voice model:", error);
      throw error;
    }
  },

  async updateVoiceModel(agentId, payload) {
    try {
      if (!agentId) {
        throw new Error("Agent ID is required");
      }
      if (!payload) {
        throw new Error("Payload is required");
      }
      let res = await transport.patch(
        `/api/voice_agents/voice_model/agent/${agentId}`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error("Error updating voice model:", error);
      throw error;
    }
  },

  async getVoiceModelSetting(provider, language) {
    try {
      if (!provider) {
        throw new Error("Provider is required");
      }
      if (!language) {
        throw new Error("Language is required");
      }
      let res = await transport.get(
        `/api/voice_agents/voice_model_setting?provider=${provider}&language=${language}`,
      );
      return res.data;
    } catch (error) {
      console.error("Error fetching voice model setting:", error);
      throw error;
    }
  },


  async fetchVoiceAgentMeetingLogs(params = {}) {
    try {
      let res = await transport.get(`/api/meeting_logs/voice_agent`, {
        params,
      });
      return res.data;
    } catch (error) {
      console.error("Error fetching voice agent meeting logs:", error);
      throw error;
    }
  },

  async createBusinessReceptionist(payload) {
    try {
      if (!payload) {
        throw new Error("Payload is required");
      }
      let res = await transport.post(
        `/api/voice_agents/business_receptionist/create_business_receptionist`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error("Error creating business receptionist:", error);
      throw error;
    }
  },

  async getBusinessReceptionists() {
    try {
      let res = await transport.get(
        `/api/voice_agents/business_receptionist`,
      );
      return res.data;
    } catch (error) {
      console.error("Error fetching business receptionists:", error);
      throw error;
    }
  },

  async updateBusinessReceptionist(receptionistId, payload) {
    try {
      if (!receptionistId) {
        throw new Error("Receptionist ID is required");
      }
      let res = await transport.patch(
        `/api/voice_agents/business_receptionist/${receptionistId}`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error("Error updating business receptionist:", error);
      throw error;
    }
  },

  async handleBusinessReceptionistInboundCall(payload) {
    try {
      if (!payload) {
        throw new Error("Payload is required");
      }
      let res = await transport.put(
        `/api/voice_agents/business_receptionist/inbound_call`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error("Error handling business receptionist inbound call:", error);
      throw error;
    }
  },
};
